import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import MediaPlayer from './MediaPlayer';

const FilePreview = ({ file, files = [file], onClose, onNavigate, hasPrevious = false, hasNext = false, loading = false, error = '' }) => {
    const dialogRef = useRef(null);
    const titleId = useId();
    const index = Math.max(0, files.findIndex(item => item.id === file.id));
    const canPrevious = index > 0 || hasPrevious;
    const canNext = index < files.length - 1 || hasNext;
    const closePreview = () => onClose(file);
    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');
    const instanceUrl = (localStorage.getItem('instanceUrl') || 'https://misskey.io').replace(/\/+$/, '');

    useEffect(() => {
        const dialog = dialogRef.current;
        const opener = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        dialog.showModal();
        document.body.style.overflow = 'hidden';
        return () => {
            if (dialog.open) dialog.close();
            document.body.style.overflow = previousOverflow;
            if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
        };
    }, []);

    useEffect(() => {
        const dialog = dialogRef.current;
        if (!dialog.contains(document.activeElement) || document.activeElement.matches(':disabled')) {
            dialog.querySelector('button').focus({ preventScroll: true });
        }
    }, [file.id, loading]);

    const moveFile = (direction) => {
        if (loading || (direction < 0 ? !canPrevious : !canNext)) return;
        onNavigate?.(direction);
    };

    const handleKeyDown = (event) => {
        if (!onNavigate || event.defaultPrevented || event.nativeEvent.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="slider"]')) return;
        const direction = { ArrowLeft: -1, ArrowRight: 1 }[event.key];
        if (!direction) return;
        event.preventDefault();
        event.stopPropagation();
        moveFile(direction);
    };

    return createPortal(
        <dialog
            ref={dialogRef}
            aria-labelledby={titleId}
            onKeyDownCapture={handleKeyDown}
            onCancel={closePreview}
            onClick={(event) => {
                if (event.target === event.currentTarget) closePreview();
            }}
            className="w-[92vw] max-w-5xl max-h-[90dvh] rounded-2xl bg-white p-0 shadow-2xl backdrop:bg-slate-950/70 backdrop:backdrop-blur-sm"
        >
            <div className="flex max-h-[90dvh] flex-col">
                <header className="flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 px-4 py-3">
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                            <h2 id={titleId} className="min-w-0 truncate font-semibold">
                                <a href={`${instanceUrl}/admin/file/${encodeURIComponent(file.id)}`} target="_blank" rel="noopener noreferrer" title="ファイルの管理画面を開く" className="block truncate text-blue-600 hover:underline focus-visible:outline-blue-600">{file.name}</a>
                            </h2>
                            <span aria-live="polite" aria-atomic="true" className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${file.isSensitive ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                                {file.isSensitive ? 'NSFW' : '通常'}
                            </span>
                        </div>
                        <p className="mt-1 truncate text-xs text-gray-500">
                            登録ユーザー: {file.user?.id ? (
                                <a href={`${instanceUrl}/admin/user/${encodeURIComponent(file.user.id)}`} target="_blank" rel="noopener noreferrer" title="ユーザーの管理画面を開く" className="text-blue-600 hover:underline focus-visible:outline-blue-600">
                                    @{file.user.username || file.user.id}{file.user.host ? '@' + file.user.host : ''}
                                </a>
                            ) : '不明'}
                        </p>
                    </div>
                    <button type="button" onClick={closePreview} className="shrink-0 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 focus-visible:outline-blue-600">
                        閉じる <kbd className="ml-1 hidden rounded border px-1 text-xs sm:inline">Esc</kbd>
                    </button>
                </header>
                <div className="flex min-h-0 items-center justify-center overflow-auto bg-slate-950 p-3 sm:p-6">
                    {(isVideo || isAudio) ? (
                        <div className={'w-full ' + (isAudio ? 'flex min-h-48 items-center py-12' : '')}>
                            <MediaPlayer key={file.id} file={file} className={isVideo ? 'max-h-[65dvh] w-full object-contain' : 'h-12 w-full'} />
                        </div>
                    ) : file.type.startsWith('image/') ? (
                        <img src={file.url} alt={file.name} className="max-h-[65dvh] max-w-full object-contain" />
                    ) : (
                        <p className="py-12 text-sm text-white">この形式は元のファイルを開いて確認できます。</p>
                    )}
                </div>
                <footer className="flex shrink-0 flex-wrap items-center justify-between gap-x-5 gap-y-2 px-4 py-3 text-sm">
                    {loading && <p role="status" className="w-full text-blue-600">前後のファイルを読み込み中...</p>}
                    {error && <p role="alert" className="w-full text-red-600">{error}</p>}
                    {onNavigate && (
                        <nav aria-label="プレビュー内のファイル移動" className="flex w-full items-center justify-between gap-2 sm:w-auto">
                            <button type="button" aria-label="前のファイル" aria-keyshortcuts="ArrowLeft" title="前のファイル（←）"
                                disabled={loading || !canPrevious} onClick={() => moveFile(-1)}
                                className="rounded-lg bg-blue-50 px-3 py-2 text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-blue-600">
                                ← <span className="hidden sm:inline">前のファイル</span>
                            </button>
                            <span aria-live="polite" aria-atomic="true" className="whitespace-nowrap px-2 text-gray-500 tabular-nums">{index + 1} / {files.length}</span>
                            <button type="button" aria-label="次のファイル" aria-keyshortcuts="ArrowRight" title="次のファイル（→）"
                                disabled={loading || !canNext} onClick={() => moveFile(1)}
                                className="rounded-lg bg-blue-50 px-3 py-2 text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-blue-600">
                                <span className="hidden sm:inline">次のファイル</span> →
                            </button>
                        </nav>
                    )}
                    <div className="flex flex-1 items-center justify-between gap-4">
                        <span className="text-gray-500">{file.type}</span>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap text-blue-600 hover:underline">元のファイルを開く ↗</a>
                    </div>
                </footer>
            </div>
        </dialog>,
        document.body
    );
};

export default FilePreview;
